import { A11yModule } from '@angular/cdk/a11y';
import { NgOptimizedImage } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { ThemeService } from '@ld.consumer-portal/utils';

/**
 * Full-screen branded interstitial shown before handing a borrower off to the Entra sign-in
 * page without a `login_hint`.
 *
 * Domains on the Entra login acceleration blocklist have to re-enter their email address on
 * the Entra page (see `EntraLoginAccelerationEvaluatorService`). Without this heads-up the second email
 * prompt looks like the first one failed, so borrowers assume the sign-in is broken and
 * abandon it or call support.
 *
 * The borrower either presses Continue to continue immediately or waits for the countdown on that
 * button to run out, whichever comes first. The Continue button takes focus as soon as the notice
 * renders, so Enter activates it without reaching for the mouse, and focus is trapped inside
 * the dialog while it is up.
 */
@Component({
  selector: 'ld-email-reentry-notice',
  imports: [A11yModule, MatProgressSpinnerModule, NgOptimizedImage, TranslocoDirective],
  templateUrl: './email-reentry-notice.component.html',
  styleUrl: './email-reentry-notice.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailReentryNoticeComponent {
  private readonly themeService = inject(ThemeService);

  /** Seconds left on the Continue button. Reaching 0 means the countdown handed off on its own. */
  readonly countdownSeconds = input.required<number>();

  /** The borrower chose to continue without waiting for the countdown. */
  readonly confirmed = output<void>();

  protected readonly ldBackground = '/assets/images/login-background.png';

  private readonly confirmButton = viewChild<ElementRef<HTMLButtonElement>>('confirmButton');

  /**
   * Tracked locally rather than inferred from `countdownSeconds`, because pressing Continue stops the
   * timer without rewinding it — the remaining seconds stay put, so they cannot tell us that a
   * hand-off is under way.
   */
  private readonly hasConfirmed = signal(false);

  protected readonly isRedirecting = computed(() => this.hasConfirmed() || this.countdownSeconds() <= 0);

  constructor() {
    // Give the borrower a keyboard path straight through the notice: focusing Continue means Enter
    // (or Space) continues, with no tabbing required.
    afterNextRender(() => this.confirmButton()?.nativeElement.focus());
  }

  /**
   * Branding is read from `ThemeService`, the same source the login page behind this notice uses,
   * so a JV tenant's logo, name, and background carry over instead of loanDepot's. `getCurrentTheme()`
   * is the service's signal-backed accessor; reading it here makes these values recompute when a
   * theme is applied or cleared.
   */
  protected readonly logoUrl = computed(() => {
    this.themeService.getCurrentTheme();
    return this.themeService.getLogoUrl();
  });

  protected readonly partnerDisplayName = computed(() => {
    this.themeService.getCurrentTheme();
    return this.themeService.getPartnerDisplayName();
  });

  protected readonly backgroundImageUrl = computed(() => {
    this.themeService.getCurrentTheme();
    const backgroundImage = this.themeService.getBackgroundImage() ?? this.ldBackground;
    return `url(${backgroundImage})`;
  });

  protected readonly footerConfig = computed(() => {
    this.themeService.getCurrentTheme();
    return this.themeService.getFooterConfig();
  });

  protected onConfirm(): void {
    if (this.isRedirecting()) {
      return;
    }
    this.hasConfirmed.set(true);
    this.confirmed.emit();
  }
}
